import type { ComponentType } from 'react';
import { isStaleChunkError, reloadForNewVersion } from '@/shared/lib/staleBuild';

const RETRY_PAUSE_MS = 400;

type Loader<K extends string> = () => Promise<Record<K, ComponentType>>;

async function loadOnce<K extends string>(load: Loader<K>, name: K): Promise<ComponentType> {
  // Старый индекс мог получить от сервера пустой/чужой модуль: проверяем явно, а не падаем на `undefined[name]`.
  const mod: unknown = await load();
  // Модуль приходит из сети и может оказаться пустым: тип «или нет» здесь честнее, чем выведенный.
  const component: ComponentType | undefined = typeof mod === 'object' && mod !== null ? Reflect.get(mod, name) : undefined;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (component === undefined) throw new Error(`Chunk load failed: в модуле нет «${name}» (Importing a module script failed)`);
  return component;
}

/**
 * Экран подгружается отдельным куском кода при первом заходе: первый экран не тянет весь Chat целиком.
 * Роутер ждёт кусок до показа экрана, поэтому мигающих заглушек нет.
 * Сбой подгрузки (сеть моргнула, после выкладки старые файлы удалены) не показывается человеку:
 * сначала повтор, потом тихое обновление страницы до новой версии; экран ошибки — только если и это не помогло.
 */
export function lz<K extends string>(load: Loader<K>, name: K): () => Promise<{ Component: ComponentType }> {
  return async () => {
    try {
      return { Component: await loadOnce(load, name) };
    } catch (first) {
      await new Promise((r) => setTimeout(r, RETRY_PAUSE_MS));
      try {
        return { Component: await loadOnce(load, name) };
      } catch (second) {
        if (isStaleChunkError(second) || isStaleChunkError(first)) {
          if (reloadForNewVersion()) return new Promise<never>(() => undefined); // страница перезагружается
        }
        throw second;
      }
    }
  };
}
